import { useQueryClient } from "@tanstack/react-query";
import { Command, useCommandState } from "cmdk";
import { Building2, Magnet, PhoneOutgoing, Sprout } from "lucide-react";
import { useMemo } from "react";

import { PaletteItem } from "@/app/shell/CommandPalette";
import { CLIENT_KINDS } from "@/features/clients/kinds";
import { clientKeys } from "@/features/clients/api";
import type { ClientRecord } from "@/features/clients/schemas";
import { unitKeys } from "@/features/units/api";
import { unitTitle, type Unit } from "@/features/units/schemas";
import { useT } from "@/shared/i18n/useT";
import { normalizeSearch } from "@/shared/lib/search";

const LIMIT = 6;

interface RecordHit {
  id: string;
  label: string;
  hint: string;
  to: string;
  icon: typeof Building2;
}

const KIND_ICONS = { lead: Magnet, "cold-call": PhoneOutgoing, farming: Sprout } as const;

/**
 * Records found by name, phone or unit code, from lists already loaded in this session (the
 * palette never fires requests of its own). Shown only once something is typed.
 */
export function PaletteRecords({ onOpen }: { onOpen: (to: string) => void }) {
  const t = useT();
  const queryClient = useQueryClient();
  const search = useCommandState((state) => state.search);
  const needle = normalizeSearch(search);
  const digits = search.replace(/\D/g, "");

  const hits = useMemo<RecordHit[]>(() => {
    if (needle.length < 2) return [];
    const results: RecordHit[] = [];
    const units = queryClient.getQueryData<Unit[]>(unitKeys.lists()) ?? [];
    for (const unit of units) {
      if (
        normalizeSearch(
          `${unit.unit_code} ${unit.project?.name ?? ""} ${unit.unit_type ?? ""}`,
        ).includes(needle)
      ) {
        results.push({
          id: `unit-${String(unit.id)}`,
          label: unit.unit_code,
          hint: unitTitle(unit),
          to: `/units/${String(unit.id)}`,
          icon: Building2,
        });
      }
    }
    for (const config of Object.values(CLIENT_KINDS)) {
      const records = queryClient.getQueryData<ClientRecord[]>(clientKeys.list(config)) ?? [];
      for (const record of records) {
        const phoneMatch =
          digits.length >= 4 &&
          (record.phone ?? "").replace(/\D/g, "").includes(digits.replace(/^0+/, ""));
        if (phoneMatch || normalizeSearch(record.name).includes(needle)) {
          results.push({
            id: `${config.kind}-${String(record.id)}`,
            label: record.name,
            hint: [t(config.singularId), record.phone].filter(Boolean).join(" · "),
            to: `/${config.path}/${String(record.id)}`,
            icon: KIND_ICONS[config.kind],
          });
        }
      }
    }
    return results.slice(0, LIMIT * 3);
  }, [needle, digits, queryClient, t]);

  if (hits.length === 0) return null;
  return (
    <Command.Group heading={t("palette.records")}>
      {hits.map((hit) => (
        <PaletteItem
          key={hit.id}
          value={`${hit.id} ${hit.label} ${search}`}
          onSelect={() => {
            onOpen(hit.to);
          }}
        >
          <hit.icon aria-hidden="true" />
          <span className="user-text min-w-0 flex-1 truncate">{hit.label}</span>
          <span className="truncate text-[12.5px] text-ink-subtle">{hit.hint}</span>
        </PaletteItem>
      ))}
    </Command.Group>
  );
}
